import Snackbar from '@mui/material/Snackbar';
import { useMemo } from 'react';
import type { SyntheticEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { usePersistProjectMigrationReport } from '../../hooks/ui/usePersistProjectMigrationReport';
import {
  clearPersistProjectMigrationReport,
  hasPersistProjectMigrationIssues,
  type PersistProjectMigrationReport,
} from '../../utils/persistMigrationReport';
import { useMeasuredBottomSnackbarStackIndex } from './SnackbarStackContext';
import { bottomCenterSnackbarStackSx, centeredPlainSnackbarContentSx, mergeSx } from './snackbarStyles';

export const PERSIST_MIGRATION_WARNING_SNACKBAR_ID = 'persist-project-migration-warning';

interface PersistMigrationWarningSnackbarProps {
  stackIndex?: number;
}

function formatFailedProjectLabels(report: PersistProjectMigrationReport): string {
  const labels = report.partialFailures
    .filter((issue) => issue.scope === 'project' || issue.scope === 'loadedProject')
    .map((issue) => issue.projectName || issue.projectId)
    .filter((label): label is string => Boolean(label));

  return [...new Set(labels)].join(', ');
}

const PersistMigrationWarningSnackbar = ({ stackIndex: stackIndexOverride }: PersistMigrationWarningSnackbarProps) => {
  const { t } = useTranslation();
  const report = usePersistProjectMigrationReport();

  const message = useMemo(() => {
    if (!hasPersistProjectMigrationIssues(report)) {
      return null;
    }

    const failedLabels = formatFailedProjectLabels(report);
    const backupNote = report.backupCreated
      ? t('persistMigration.snackbar.backupSaved', ' A local backup of your previous project data was saved.')
      : '';

    if (report.catastrophicFailure) {
      return t(
        'persistMigration.snackbar.catastrophic',
        'Your saved projects could not be fully updated. Your previous data was restored instead.{{backupNote}}',
        { backupNote }
      );
    }

    if (failedLabels) {
      return t(
        'persistMigration.snackbar.partialWithNames',
        'Some projects could not be updated automatically ({{names}}). Your original copies were kept.{{backupNote}}',
        { names: failedLabels, backupNote }
      );
    }

    return t('persistMigration.snackbar.partial', 'Some projects could not be updated automatically. Your original copies were kept.{{backupNote}}', {
      backupNote,
    });
  }, [report, t]);

  const open = Boolean(message);
  const { ref: snackbarRef, stackIndex: registeredStackIndex } = useMeasuredBottomSnackbarStackIndex<HTMLDivElement>({
    disabled: stackIndexOverride !== undefined,
    horizontal: 'center',
    open,
  });
  const stackIndex = stackIndexOverride ?? registeredStackIndex;

  const handleClose = (_event: Event | SyntheticEvent, reason: string) => {
    if (reason === 'clickaway') {
      return;
    }
    clearPersistProjectMigrationReport();
  };

  return (
    <Snackbar
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      ref={snackbarRef}
      open={open}
      autoHideDuration={null}
      onClose={handleClose}
      message={message}
      sx={bottomCenterSnackbarStackSx(stackIndex)}
      slotProps={{
        content: {
          id: PERSIST_MIGRATION_WARNING_SNACKBAR_ID,
          role: 'alert',
          sx: mergeSx(centeredPlainSnackbarContentSx, (theme) => ({
            maxWidth: 420,
            backgroundColor: theme.vars?.palette?.background?.paper ?? theme.palette.background.paper,
            color: theme.vars?.palette?.warning?.main ?? theme.palette.warning.main,
          })),
        },
      }}
    />
  );
};

export default PersistMigrationWarningSnackbar;
